import { toast } from "react-toastify";

export const ADMISSION_FILE_MAX_BYTES = 5 * 1024 * 1024;
export const ADMISSION_TOAST = { position: "top-center", autoClose: 3000 };

const GENDERS = ["Male", "Female", "Transgender"];
const STATUSES = ["Active", "Deactive", "Paid", "Failed"];
const MIN_NAME_LEN = 2;
const MAX_NAME_LEN = 100;
const MIN_AGE_YEARS = 3;
const MAX_AGE_YEARS = 100;
const MAX_EMAIL_LEN = 254;
const MIN_OPTIONAL_SCHOOL_LEN = 2;
const MAX_SCHOOL_LEN = 200;
const MIN_OPTIONAL_ADDRESS_LEN = 5;
const MAX_ADDRESS_LEN = 500;

const EMAIL_RE =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const MOBILE_IN_RE = /^[6-9]\d{9}$/;
const PINCODE_RE = /^\d{6}$/;
const NAME_CHARS_RE = /^[\p{L}\p{M}\s.'-]+$/u;

export const trimField = (value) => String(value ?? "").trim();

function getAgeYears(dob) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age -= 1;
    }
    return age;
}

function pushNameErrors(errors, label, value, required = true) {
    const v = trimField(value);
    if (!v) {
        if (required) errors.push(`${label} is required.`);
        return;
    }
    if (v.length < MIN_NAME_LEN) {
        errors.push(`${label} must be at least ${MIN_NAME_LEN} characters.`);
        return;
    }
    if (v.length > MAX_NAME_LEN) {
        errors.push(`${label} must be at most ${MAX_NAME_LEN} characters.`);
        return;
    }
    if (/\d/.test(v)) {
        errors.push(`${label} cannot contain numbers.`);
        return;
    }
    if (!NAME_CHARS_RE.test(v)) {
        errors.push(`${label} contains invalid characters.`);
    }
}

function pushDobErrors(errors, dobStr) {
    if (!dobStr) {
        errors.push("Date of birth is required.");
        return;
    }
    const dob = new Date(`${dobStr}T00:00:00`);
    if (Number.isNaN(dob.getTime())) {
        errors.push("Enter a valid date of birth.");
        return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dob > today) {
        errors.push("Date of birth cannot be in the future.");
        return;
    }
    const age = getAgeYears(dob);
    if (age < MIN_AGE_YEARS) {
        errors.push(`Student must be at least ${MIN_AGE_YEARS} years old.`);
        return;
    }
    if (age > MAX_AGE_YEARS) {
        errors.push("Please enter a valid date of birth.");
    }
}

function pushEmailErrors(errors, email) {
    const v = trimField(email);
    if (!v) {
        errors.push("Email is required.");
        return;
    }
    if (v.length > MAX_EMAIL_LEN) {
        errors.push(`Email must be at most ${MAX_EMAIL_LEN} characters.`);
        return;
    }
    if (!EMAIL_RE.test(v)) {
        errors.push("Enter a valid email address.");
    }
}

function pushMobileErrors(errors, mobile) {
    const v = trimField(mobile);
    if (!v) {
        errors.push("Phone number is required.");
        return;
    }
    if (!MOBILE_IN_RE.test(v)) {
        errors.push("Enter a valid 10-digit Indian mobile number (starts with 6–9).");
    }
}

function pushOptionalPincodeErrors(errors, pincode) {
    const v = trimField(pincode);
    if (!v) return;
    if (!PINCODE_RE.test(v)) {
        errors.push("Pincode must be exactly 6 digits.");
    }
}

function pushOptionalSchoolErrors(errors, school) {
    const v = trimField(school);
    if (!v) return;
    if (v.length < MIN_OPTIONAL_SCHOOL_LEN) {
        errors.push(`School name must be at least ${MIN_OPTIONAL_SCHOOL_LEN} characters.`);
        return;
    }
    if (v.length > MAX_SCHOOL_LEN) {
        errors.push(`School name must be at most ${MAX_SCHOOL_LEN} characters.`);
    }
}

function pushOptionalAddressErrors(errors, address) {
    const v = trimField(address);
    if (!v) return;
    if (v.length < MIN_OPTIONAL_ADDRESS_LEN) {
        errors.push(`Street address must be at least ${MIN_OPTIONAL_ADDRESS_LEN} characters.`);
        return;
    }
    if (v.length > MAX_ADDRESS_LEN) {
        errors.push(`Street address must be at most ${MAX_ADDRESS_LEN} characters.`);
    }
}

/**
 * Validate file on pick or before submit. Returns error message or null.
 */
export function validateAdmissionUploadFile(file, { imageOnly = false } = {}) {
    if (!file) return null;
    if (imageOnly && !file.type.startsWith("image/")) {
        return "Please choose an image file (JPG, PNG, or WebP).";
    }
    if (!imageOnly) {
        const ok = file.type.startsWith("image/") || file.type === "application/pdf";
        if (!ok) {
            return "Please choose an image or PDF for the Aadhaar document.";
        }
    }
    if (file.size > ADMISSION_FILE_MAX_BYTES) {
        return "File must be 5 MB or smaller.";
    }
    return null;
}

/**
 * Collect all admission form validation errors (required + optional field formats).
 */
export function getAdmissionFormValidationErrors(formData, { batchesList = [] } = {}) {
    const errors = [];

    pushNameErrors(errors, "Full name", formData.name, true);
    pushNameErrors(errors, "Father's name", formData.fatherName, true);
    pushNameErrors(errors, "Mother's name", formData.motherName, true);
    pushDobErrors(errors, formData.dob);

    const gender = trimField(formData.gender);
    if (!gender) {
        errors.push("Gender is required.");
    } else if (!GENDERS.includes(gender)) {
        errors.push("Select a valid gender.");
    }

    pushEmailErrors(errors, formData.email);
    pushMobileErrors(errors, formData.mobile);
    pushOptionalSchoolErrors(errors, formData.school);
    pushOptionalAddressErrors(errors, formData.address);
    pushOptionalPincodeErrors(errors, formData.pincode);

    const center = trimField(formData.center);
    if (!center) {
        errors.push("Center is required.");
    }

    if (
        center &&
        batchesList.length > 0 &&
        (formData.batch_id === "" ||
            formData.batch_id == null ||
            Number.isNaN(parseInt(formData.batch_id, 10)))
    ) {
        errors.push("Select a batch for this student.");
    }

    const status = trimField(formData.status);
    if (!status) {
        errors.push("Status is required.");
    } else if (!STATUSES.includes(status)) {
        errors.push("Select a valid status.");
    }

    return errors;
}

export function showAdmissionValidationErrors(errors) {
    if (!errors?.length) return false;
    toast.error(errors[0], ADMISSION_TOAST);
    return true;
}

export function validateAdmissionForm(formData, options) {
    const errors = getAdmissionFormValidationErrors(formData, options);
    if (errors.length) {
        showAdmissionValidationErrors(errors);
        return false;
    }
    return true;
}
